import type { Status } from '@ausbom/form/lib/types';
import type { GlobalAlert } from '../api/globalAlerts.types';

export const ALERTS_DISMISSED_STORAGE_KEY = 'alerts:dismissed';

const isValidDate = (date: Date): boolean => date instanceof Date && !isNaN(date.getTime());

// Parse "YYYY-MM-DDTHH:mm:ss" and adds Z if missing (timezone-safe)
export const convertStringToDate = (date?: string): Date | null => {
  if (!date) return null;
  const cDate = new Date(Date.parse(/[zZ]|[+-]\d{2}:\d{2}$/.test(date) ? date : date.trim() + 'Z'));
  return isValidDate(cDate) ? cDate : null;
};

export const filterAlerts = (alerts: Array<GlobalAlert>, currentEnv?: string): Array<GlobalAlert> => {
  const now = new Date();
  return alerts.filter((alert) => {
    if (alert.enabled === false) return false;
    if (alert?.from_date) {
      const fromDate = convertStringToDate(alert.from_date);
      if (fromDate && fromDate > now) return false;
    }
    if (alert?.to_date) {
      const toDate = convertStringToDate(alert.to_date);
      if (toDate && toDate < now) return false;
    }
    if (alert?.envs?.length && currentEnv) {
      if (!alert.envs.includes(currentEnv)) return false;
    }
    return true;
  });
};

export const sortAlerts = (alerts: Array<GlobalAlert>): Array<GlobalAlert> => {
  const BASE: Record<Status, number> = { warning: 0, error: 2, failure: 4, info: 6, success: 8 };
  const priority = (alert: GlobalAlert) => BASE[alert.alert_type] + (alert.dismissible ? 1 : 0);

  return [...alerts].sort((a, b) => {
    const pa = priority(a);
    const pb = priority(b);
    return pa !== pb ? pa - pb : 0;
  });
};

export const storeDismissedAlertKey = (alertId: string): void => {
  if (!alertId?.trim()) {
    console.warn('Could not store dismissed alert key, invalid input');
    return;
  }
  const existing = fetchDismissedAlertKeys();
  try {
    localStorage.setItem(ALERTS_DISMISSED_STORAGE_KEY, JSON.stringify([...existing, alertId]));
  } catch (e) {
    console.warn('Could not store dismissed alert key:', e instanceof Error ? e.message : e);
  }
};

export const fetchDismissedAlertKeys = (): Array<string> => {
  try {
    const json = JSON.parse(localStorage.getItem(ALERTS_DISMISSED_STORAGE_KEY) || '[]');
    if (!Array.isArray(json)) return [];
    return json.every((el) => typeof el === 'string' && el.length > 0) ? json : [];
  } catch {
    return [];
  }
};
